import React from 'react';
import { Navigate } from 'react-router-dom';
import { canAccess } from '@ams/shared/module-access';
import { useAuth } from '../auth/AuthContext.js';
import { MODULES } from './modules.js';

/**
 * Index-route redirector.
 *
 * Picks the first MODULES entry accessible to the current user and <Navigate>s
 * there. Settings is always accessible for signed-in users (see module-access.ts),
 * so the fallback branch is defensive only — it should not fire in practice.
 */
export function ResolveHome(): JSX.Element {
  const { status, payload } = useAuth();

  // Wait for auth to finish loading before redirecting
  if (status === 'loading') {
    return <div />;
  }

  const privileges = payload?.privileges ?? [];
  // Settings is always accessible (see module-access.ts) and is the defensive
  // fallback — not a regular first-match target. Skip it during the picker pass
  // so an admin-capable user without expoams access lands on /admin, not /settings.
  const accessible = MODULES.filter((m) => m.id !== 'settings' && canAccess(m.id, privileges));
  // Prefer an in-SPA module as the landing target. An external module (its own
  // Vite entry, e.g. Commercial Console) renders no AppShell nav bar, so landing
  // there strands the user outside the shell with every other tab unreachable
  // (the container's only link back re-runs this resolver → redirect loop). Fall
  // back to an external module only when it is the user's ONLY accessible one.
  const first = accessible.find((m) => !m.external) ?? accessible[0];
  // An external module is a separate site (its own Vite entry) — a
  // react-router <Navigate> can't reach it, so leave the SPA entirely.
  if (first?.external) {
    window.location.replace(first.path);
    return <div />;
  }
  return <Navigate to={first?.path ?? '/settings'} replace />;
}
